import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  Calendar,
  MessageCircle,
  Mail,
  Clock,
  Shield,
  Star,
  ArrowRight,
} from "lucide-react";
import { useEffect, useState } from "react";

export default function SuccessStep() {
  const meetingsLink = import.meta.env.VITE_MEETINGS_LINK || "https://calendly.com/jemma-dental";

  const [referenceId, setReferenceId] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored =
        localStorage.getItem("intakeId") || localStorage.getItem("referenceId");
      if (stored && stored.trim().length > 0) {
        setReferenceId(stored);
      } else {
        const generated = Math.random()
          .toString(36)
          .substring(2, 11)
          .toUpperCase();
        setReferenceId(generated);
        try {
          localStorage.setItem("referenceId", generated);
        } catch (_) {
          // Ignore write errors
        }
      }
    } catch (_) {
      // Ignore localStorage access errors
    }
  }, []);

  const handleBookConsultation = () => {
    window.open(meetingsLink, "_blank");
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="h-full flex flex-col lg:pt-4">
      {/* Mobile: Traditional layout */}
      <div className="lg:hidden space-y-6">
        {/* Success Icon */}
        <div className="text-center space-y-4">
          <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto shadow-lg">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <div>
            <h2 className="heading-secondary text-gray-900 mb-2">
              Intake <span style={{ color: "#2E86C1" }}>Complete</span>!
            </h2>
            <p
              style={{ color: "#F39C12" }}
              className="font-heading font-bold text-lg mb-3"
            >
              Your Journey to Paradise Begins Now
            </p>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 mb-4">
              <p className="text-gray-700 text-sm leading-relaxed font-medium">
                <strong>Congratulations!</strong> Your intake is complete and
                our expert Colombian dental team is already reviewing your
                information to create your personalized treatment plan.
              </p>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-r from-[#2E86C1]/10 to-[#F39C12]/10 rounded-xl p-6 space-y-4">
          <h3 className="font-heading font-semibold text-gray-900 flex items-center justify-center gap-2 text-base">
            <Clock className="w-5 h-5 text-[#2E86C1]" />
            What Happens Next?
          </h3>

          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#2E86C1] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                1
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  Expert Review & Analysis
                </p>
                <p className="text-gray-600">
                  Our Colombian dental specialists analyze your photos and
                  medical information
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#F39C12] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                2
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  Personal Contact Within 24-48 business Hours
                </p>
                <p className="text-gray-600">
                  A Jema Services specialist will reach out with your
                  preliminary assessment
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#CEF12F] text-gray-900 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                3
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  Complete Treatment & Travel Plan
                </p>
                <p className="text-gray-600">
                  Receive your personalized dental plan plus Colombian vacation
                  package
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main CTA */}
        <div className="space-y-3">
          <Button
            onClick={handleBookConsultation}
            className="w-full shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2 px-4 py-3 text-base xl:text-lg"
            variant="jema"
            size="xl"
          >
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span className="truncate">Book Your Free Virtual Consult</span>
            </span>
          </Button>

          <p className="text-xs text-gray-500 text-center">
            30-minute consultation • No commitment required • Expert guidance
          </p>
        </div>

        {/* Benefits Section */}
        <div className="bg-white rounded-lg border border-gray-100 p-4 space-y-4">
          <h4 className="font-heading font-semibold text-gray-900 text-center">
            What You'll Receive:
          </h4>

          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-[#2E86C1]" />
              <span className="text-gray-700">
                Confirmation email with submission details
              </span>
            </div>

            <div className="flex items-center gap-3">
              <MessageCircle className="w-4 h-4 text-green-600" />
              <span className="text-gray-700">
                Personal contact within 24-48 business hours
              </span>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-[#F39C12]" />
              <span className="text-gray-700">
                Virtual consultation invitation
              </span>
            </div>

            <div className="flex items-center gap-3">
              <Star className="w-4 h-4 text-purple-600" />
              <span className="text-gray-700">
                Colombian dental tourism package options
              </span>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-gradient-to-r from-[#2E86C1]/10 to-[#2E86C1]/5 border border-[#2E86C1]/20 rounded-lg p-3">
          <div className="flex items-center gap-2 justify-center">
            <Shield className="w-4 h-4 text-[#2E86C1]" />
            <p className="text-xs text-[#2E86C1] font-medium">
              Secure & Encrypted • Reference:{" "}
              {referenceId ? `#${referenceId}` : "#—"}
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="text-xs text-gray-500 space-y-1 text-center">
          <p className="font-medium">Questions? We're here to help:</p>
          <p>
            📧{" "}
            <a
              href="mailto:support@jemasvcs.com"
              className="text-[#2E86C1] underline hover:text-[#166B93] font-medium"
            >
              support@jemasvcs.com
            </a>
          </p>
          <p>
            📞{" "}
            <a
              href="tel:9542819852"
              className="text-[#2E86C1] underline hover:text-[#166B93] font-medium"
            >
              954-281-9852
            </a>
          </p>
          <p className="text-[#2E86C1] font-medium">
            Response within 24-48 business hours
          </p>
        </div>

        {/* Alternative Actions */}
        <div className="pt-4 border-t border-gray-100 pb-10">
          <Button
            variant="outline"
            onClick={handleGoHome}
            className="w-full border-2 hover:bg-gray-50"
          >
            Return to Homepage
          </Button>
        </div>
      </div>

      {/* Desktop/Tablet: Modern full-screen layout */}
      <div className="hidden lg:block max-w-2xl xl:max-w-3xl mx-auto">
        <div className="space-y-8 xl:space-y-12">
          {/* Success Header - Centered and prominent */}
          <div className="text-center space-y-6">
            <div className="w-32 h-32 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto shadow-2xl">
              <CheckCircle className="w-16 h-16 text-white" />
            </div>
            <div>
              <h2 className="heading-primary text-gray-900 leading-tight mb-4">
                Intake <span className="text-[#166B93]">Complete</span>!
              </h2>
              <p className="heading-tertiary text-[#FF9142] font-semibold mb-6">
                Your Colombian Dental Adventure Awaits
              </p>
              <div className="max-w-2xl mx-auto bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                <p className="body-base text-gray-700 leading-relaxed">
                  <strong>Congratulations!</strong> Your comprehensive intake is
                  complete. Our world-class Colombian dental team is already
                  analyzing your information to create a personalized treatment
                  plan that combines premium dental care with an unforgettable
                  vacation experience.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-8 xl:gap-12">
            {/* Left Column: Next Steps */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">
              <div className="text-center">
                <h3 className="heading-tertiary text-gray-900 mb-2">
                  What Happens Next?
                </h3>
                <p className="body-base text-gray-600">
                  Your journey to the perfect smile
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#166B93] text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-gray-900 mb-1">
                      Expert Review & Analysis
                    </p>
                    <p className="body-small text-gray-600">
                      Our Colombian dental specialists perform a comprehensive
                      analysis of your photos and medical history
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF9142] text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-gray-900 mb-1">
                      Personal Contact Within 24-48 business Hours
                    </p>
                    <p className="body-small text-gray-600">
                      A Jema Services specialist contacts you with preliminary
                      assessment and next steps
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#CEF12F] text-gray-900 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-gray-900 mb-1">
                      Complete Treatment & Travel Plan
                    </p>
                    <p className="body-small text-gray-600">
                      Receive your detailed dental treatment plan plus curated
                      Colombian vacation package
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Benefits & CTA */}
            <div className="space-y-6">
              {/* Main CTA */}
              <div className="bg-gradient-to-br from-[#166B93]/5 to-[#FF9142]/5 rounded-2xl p-8 text-center space-y-6">
                <div>
                  <h3 className="heading-tertiary text-gray-900 mb-2">
                    Ready for Your Consultation?
                  </h3>
                  <p className="body-base text-gray-600">
                    Book your complimentary virtual consultation with our dental
                    experts
                  </p>
                </div>

                <Button
                  onClick={handleBookConsultation}
                  className="w-full shadow-lg hover:shadow-xl transform hover:scale-105"
                  variant="jema"
                  size="xl"
                >
                  <Calendar className="w-6 h-6 mr-3" />
                  Book Your Free Virtual Consult
                  <ArrowRight className="w-5 h-5 ml-3" />
                </Button>

                <p className="body-small text-gray-500">
                  30-minute consultation • No commitment required • Expert
                  guidance • Tourism planning included
                </p>
              </div>

              {/* Benefits */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
                <h4 className="font-heading font-semibold text-gray-900 text-center mb-4">
                  What You'll Receive:
                </h4>

                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Mail className="w-5 h-5 text-[#166B93]" />
                    <span className="body-small text-gray-700">
                      Detailed confirmation email with submission summary
                    </span>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                    <span className="body-small text-gray-700">
                      Personal contact within 24-48 business hours
                    </span>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Calendar className="w-5 h-5 text-[#FF9142]" />
                    <span className="body-small text-gray-700">
                      Virtual consultation scheduling invitation
                    </span>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Star className="w-5 h-5 text-purple-600" />
                    <span className="body-small text-gray-700">
                      Colombian dental tourism package options
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-8">
            {/* Security Notice */}
            <div className="bg-gradient-to-r from-[#166B93]/10 to-[#166B93]/5 border border-[#166B93]/20 rounded-2xl p-6">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Shield className="w-6 h-6 text-[#166B93]" />
                <h4 className="font-heading font-semibold text-[#166B93]">
                  Secure & Protected
                </h4>
              </div>
              <p className="body-small text-[#166B93] text-center">
                Your information is encrypted with bank-level security
                <br />
                Reference ID:{" "}
                <span className="font-mono font-bold">
                  {referenceId ? `#${referenceId}` : "#—"}
                </span>
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-2xl p-6 text-center">
              <h4 className="font-heading font-semibold text-gray-900 mb-4">
                Need Assistance?
              </h4>
              <div className="space-y-2 body-small text-gray-600">
                <p>
                  📧{" "}
                  <a
                    href="mailto:support@jemasvcs.com"
                    className="text-[#166B93] font-medium underline hover:text-[#0d4d6c] transition-colors"
                  >
                    support@jemasvcs.com
                  </a>
                </p>
                <p>
                  📞{" "}
                  <a
                    href="tel:9542819852"
                    className="text-[#166B93] font-medium underline hover:text-[#0d4d6c] transition-colors"
                  >
                    954-281-9852
                  </a>
                </p>
                <p className="text-[#FF9142] font-semibold mt-3">
                  We respond within 24-48 business hours
                </p>
              </div>
            </div>
          </div>

          {/* Alternative Action */}
          <div className="text-center pt-8 border-t border-gray-100 pb-10">
            <Button
              variant="outline"
              onClick={handleGoHome}
              className="px-8 py-3 border-2 hover:bg-gray-50 rounded-xl"
            >
              Return to Homepage
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
